import React, { useMemo } from 'react';
import { useGlobal } from '../context/GlobalContext';
import { Ubicacion } from '../types';
import '../styles/MapaBodega.css';

const MapaBodega: React.FC = () => {
    const { data, loading } = useGlobal();
    const ubicaciones: Ubicacion[] = data?.ubicaciones || [];

    // Group locations by Pasillo (first part of name before the dash, e.g. A in A-03-02)
    const agrupadasPorPasillo = useMemo(() => {
        const agrupar: Record<string, Ubicacion[]> = {};
        ubicaciones.forEach(u => {
            const nombreSeguro = u?.nombre || 'Desconocido';
            const pasillo = nombreSeguro.split('-')[0] || 'Desconocido';
            if (!agrupar[pasillo]) {
                agrupar[pasillo] = [];
            }
            agrupar[pasillo].push(u);
        });

        // Sort keys alphabetically
        const sortedKeys = Object.keys(agrupar).sort();
        const sortedAgrupar: Record<string, Ubicacion[]> = {};
        sortedKeys.forEach(k => {
            // Further sort locations within a pasillo by modulo and then level
            const currentGroup = agrupar[k] || [];
            sortedAgrupar[k] = currentGroup.sort((a, b) => {
                const nameA = a?.nombre || '';
                const nameB = b?.nombre || '';
                return nameA.localeCompare(nameB);
            });
        });
        return sortedAgrupar;
    }, [ubicaciones]);

    const getZoneColor = (zona: 'A' | 'B' | 'C' | string) => {
        switch (zona) {
            case 'A': return '#ef4444'; // Red for fast moving
            case 'B': return '#f59e0b'; // Yellow/Orange
            case 'C': return '#3b82f6'; // Blue for slow moving
            default: return '#6b7280'; // Gray
        }
    };

    if (loading) {
        return <div className="p-4">Cargando mapa...</div>;
    }

    return (
        <div className="mapa-bodega-page">
            <h2 className="text-2xl font-bold mb-4">Mapa de Bodega (Slotting)</h2>

            <div className="leyenda mb-6 flex gap-4">
                <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: getZoneColor('A') }}></span>
                    <span>Zona A (Alta rotación)</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: getZoneColor('B') }}></span>
                    <span>Zona B (Media rotación)</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: getZoneColor('C') }}></span>
                    <span>Zona C (Baja rotación)</span>
                </div>
            </div>

            <div className="pasillos-container">
                {Object.keys(agrupadasPorPasillo).length === 0 ? (
                    <p>No hay ubicaciones creadas aún.</p>
                ) : (
                    Object.keys(agrupadasPorPasillo).map(pasillo => (
                        <div key={pasillo} className="pasillo-section mb-8 p-4 border rounded-lg bg-white shadow-sm">
                            <h3 className="text-xl font-bold mb-4 border-b pb-2">Pasillo {pasillo}</h3>
                            <div className="ubicaciones-grid gap-3">
                                {(agrupadasPorPasillo[pasillo] || []).map(u => (
                                    <div
                                        key={u.id}
                                        className="ubicacion-cell p-3 rounded shadow text-center border-l-4"
                                        style={{ borderLeftColor: getZoneColor(u.zona_abc) }}
                                    >
                                        <div className="font-bold">{u.nombre}</div>
                                        <div className="text-sm text-gray-500">Zona {u.zona_abc}</div>
                                        {u.descripcion && (
                                            <div className="text-xs text-gray-400 mt-1 truncate" title={u.descripcion}>
                                                {u.descripcion}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default MapaBodega;
